import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook for fetching paginated data with smart debouncing
 * Separates data fetching from UI state management
 */
export const usePaginatedData = (fetchFunction, paginationState, options = {}) => {
    const {
        debounceMs = 300,
        enableClientFiltering = false,
        searchFields = []
    } = options;

    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Use refs to avoid re-render triggers
    const searchTimeoutRef = useRef(null);
    const lastSearchRef = useRef('');
    const isInitialLoadRef = useRef(true);

    // Stable error setter
    const setErrorCallback = useCallback((error) => {
        setError(error);
    }, []);

    // Main data fetching function
    const fetchData = useCallback(async (params, skipLoading = false) => {
        try {
            if (!skipLoading) {
                setLoading(true);
            }
            setError(null);

            const result = await fetchFunction(params);
            
            if (result && result.data && result.pagination) {
                setData(result.data);
                setPagination(result.pagination);
            } else {
                // Fallback for non-paginated responses
                setData(Array.isArray(result) ? result : []);
                setPagination(null);
            }
        } catch (err) {
            setError(err.message || 'Failed to fetch data');
            setData([]);
            setPagination(null);
        } finally {
            if (!skipLoading) {
                setLoading(false);
            }
        }
    }, [fetchFunction]);

    // Handle immediate pagination changes (page, limit, sort, order)
    useEffect(() => {
        const params = {
            page: paginationState.page,
            limit: paginationState.limit,
            sort: paginationState.sort,
            order: paginationState.order,
            ...(lastSearchRef.current && { search: lastSearchRef.current })
        };

        fetchData(params);
    }, [
        paginationState.page,
        paginationState.limit,
        paginationState.sort,
        paginationState.order,
        fetchData
    ]);

    // Handle debounced search
    useEffect(() => {
        const currentSearch = paginationState.search;

        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Skip debouncing on initial load
        if (isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
            lastSearchRef.current = currentSearch;
            return;
        }

        // Skip if search hasn't changed
        if (currentSearch === lastSearchRef.current) {
            return;
        }

        // Debounce search
        searchTimeoutRef.current = setTimeout(() => {
            lastSearchRef.current = currentSearch;
            const params = {
                page: paginationState.page,
                limit: paginationState.limit,
                sort: paginationState.sort,
                order: paginationState.order,
                ...(currentSearch && { search: currentSearch })
            };
            fetchData(params);
        }, debounceMs);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [paginationState.search, fetchData, debounceMs, paginationState]);

    // Client-side filtering for small datasets
    const filteredData = enableClientFiltering && searchFields.length > 0 && paginationState.search
        ? data.filter(item =>
            searchFields.some(field =>
                String(item[field] || '').toLowerCase().includes(paginationState.search.toLowerCase())
            )
        )
        : data;

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    return {
        data: filteredData,
        pagination,
        loading,
        error,
        setError: setErrorCallback,
        refetch: () => {
            const params = {
                page: paginationState.page,
                limit: paginationState.limit,
                sort: paginationState.sort,
                order: paginationState.order,
                ...(paginationState.search && { search: paginationState.search })
            };
            return fetchData(params);
        }
    };
};